/**
 * TopBarReportActions — Edit + print buttons + sign-in pill rendered in
 * the top bar (`MainViewHeader` children). Moved here from
 * `ReportHeader` so the hero stays clean and these controls are reachable
 * without scrolling.
 *
 * Print options modal (the "include solutions" checkbox) still belongs to
 * ReportHeader for now — the print flow itself is wired to the hero's
 * existing handler. We just trigger it from up here.
 */
import React from 'react';
import styled from '@emotion/styled';
import { Pencil, Check, Printer } from 'lucide-react';
import { useReportEdits } from './ReportEditsContext';
import SignInButton from './SignInButton';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import ReportLanguageSwitcher from './ReportLanguageSwitcher';

const Row = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${p => p.$active ? '#fff' : 'rgba(255, 255, 255, 0.12)'};
  color: ${p => p.$active ? '#111' : '#fff'};
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: ${p => p.$active ? '#f5f5f5' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

export default function TopBarReportActions({ onPrint }) {
  const { canEdit, editMode, setEditMode, isTranslatedView } = useReportEdits();
  const { t } = useTranslation();
  return (
    <Row>
      <ReportLanguageSwitcher />
      {canEdit && !isTranslatedView && (
        <Btn
          $active={editMode}
          onClick={() => setEditMode(!editMode)}
          aria-pressed={editMode}
          title={editMode ? t('report.topbar.editStopTitle') : t('report.topbar.editTitle')}
        >
          {editMode ? <Check size={14} /> : <Pencil size={14} />}
          {editMode ? t('report.topbar.editStop') : t('report.topbar.edit')}
        </Btn>
      )}
      <Btn onClick={onPrint} title={t('report.topbar.printPdf')}>
        <Printer size={14} />
        {t('report.topbar.print')}
      </Btn>
      <SignInButton />
    </Row>
  );
}
